import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import './Loading.css';

const MovieLoading = ({ 
  theme = 'movie',
  message = null,
  size = 'medium',
  fullScreen = true 
}) => {
  const [currentMessage, setCurrentMessage] = useState('');

  // Theme-specific configurations
  const themes = {
    movie: {
      messages: [
        "Rolling the credits...",
        "Preparing your popcorn... 🍿",
        "Dimming the lights...",
        "Setting the scene... 🎭",
        "Buffering the magic... ✨",
        "Finding your streaming link...",
        "Warming up the projector... 🎥",
        "Selecting the best seat...",
        "Threading the film reel... 🎞️",
        "Almost there... the wait is worth it!"
      ],
      icon: "🎬",
      className: "theme-movie"
    },
    series: {
      messages: [
        "Loading next episode...",
        "Getting binge-ready... 📺",
        "Preparing season finale...",
        "Loading episode recap...",
        "Setting up autoplay...",
        "Finding where you left off...",
        "Preparing subtitles...",
        "Skipping intro...",
        "Loading next episode in 3... 2...",
        "Queueing up the season..."
      ],
      icon: "📺",
      className: "theme-series"
    },
    simple: {
      messages: ["Loading...", "Please wait...", "Almost ready..."],
      icon: "⏳",
      className: "theme-simple"
    }
  };

  // Get active theme
  const activeTheme = themes[theme] || themes.movie;

  // Rotate messages if no custom message provided
  useEffect(() => {
    if (message) {
      setCurrentMessage(message);
      return;
    }

    const interval = setInterval(() => {
      const randomIndex = Math.floor(Math.random() * activeTheme.messages.length);
      setCurrentMessage(activeTheme.messages[randomIndex]);
    }, 2000);

    // Set initial message
    const initialIndex = Math.floor(Math.random() * activeTheme.messages.length);
    setCurrentMessage(activeTheme.messages[initialIndex]);

    return () => clearInterval(interval);
  }, []);

  // Size classes
  const sizeClasses = {
    small: 'size-small',
    medium: 'size-medium',
    large: 'size-large'
  };

  const containerClass = `
    loading-screen 
    ${activeTheme.className} 
    ${sizeClasses[size]} 
    ${fullScreen ? 'full-screen' : 'inline'}
  `.trim();

  return (
    <div className={containerClass} role="status" aria-live="polite">
      <div className="loading-content">
        
        {/* Animated Icon */}
        <div className="loading-icon">
          <div className="icon-wrapper">
            <span className="theme-icon" aria-hidden="true">
              {activeTheme.icon}
            </span>
            <div className="icon-ring"></div>
          </div>
        </div>

        {/* Loading Message */}
        <div className="loading-message">
          <h3 className="message-text">{currentMessage}</h3>
        </div>

        {/* Loading Dots Animation */}
        <div className="loading-dots" aria-hidden="true">
          <div className="dot"></div>
          <div className="dot"></div>
          <div className="dot"></div>
        </div>

      </div>
    </div>
  );
};

MovieLoading.propTypes = {
  theme: PropTypes.oneOf(['movie', 'series', 'simple']),
  message: PropTypes.string,
  showProgress: PropTypes.bool,
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  fullScreen: PropTypes.bool
};

export default MovieLoading;
